<script setup lang="ts">
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/24/outline';
import type { Dayjs } from 'dayjs';
import { vOnClickOutside } from '@vueuse/components';

const props = defineProps<{
  appendTo: string | HTMLElement;
  bgColor: string;
  canGoNextMonth: boolean;
  canGoPrevMonth: boolean;
  canGoNextYear: boolean;
  canGoPrevYear: boolean;
  closeOnSelect: boolean;
  dayNames: string[];
  disabled: boolean;
  format: string;
  grid: {
    date: Dayjs;
    disabled: boolean;
    inCurrentMonth: boolean;
    isToday: boolean;
  }[];
  isRange: boolean;
  isSelectedEnd: (d: Dayjs) => boolean;
  isSelectedStart: (d: Dayjs) => boolean;
  isWithinSelected: (d: Dayjs) => boolean;
  modelRange: { endDate: Dayjs | null; startDate: Dayjs | null };
  modelSingle: Dayjs | null;
  monthNames: string[];
  monthOptions: { disabled: boolean; label: string; value: number }[];
  open: boolean;
  panelStyle: Record<string, string | number>;
  showFooterConfirm: boolean;
  viewMode: 'days' | 'months' | 'years';
  viewMonth: number;
  viewYear: number;
  yearOptions: { disabled: boolean; label: string; value: number }[];
}>();
const datePickerRoot = inject<Ref<HTMLElement>>('datePickerRoot');
const panel = ref<HTMLElement>();
const emits = defineEmits<{
  (e: 'prev'): void;
  (e: 'next'): void;
  (e: 'prev-year'): void;
  (e: 'next-year'): void;
  (e: 'toggle-month'): void;
  (e: 'toggle-year'): void;
  (e: 'choose-month', value: number): void;
  (e: 'choose-year', value: number): void;
  (e: 'day-click', d: Dayjs): void;
  (e: 'clear'): void;
  (e: 'cancel'): void;
  (e: 'confirm'): void;
  (e: 'reset', close?: boolean): void;
  (e: 'close'): void;
}>();
const handleClickOutside = () => {
  if (panel.value) emits('close');
};
const getDayContainerClasses = (d: Dayjs) => [
  'relative text-xs font-light items-center justify-center w-1/2 z-5',
  'disabled:opacity-30 disabled:cursor-not-allowed disabled:!bg-transparent capitalize',
  {
    'bg-primary-blue-gradient text-light-50':
      (props.isRange && (props.isSelectedStart(d) || props.isSelectedEnd(d))) ||
      props.modelSingle?.isSame(d, 'day'),
    'rounded-l-none':
      props.isRange && props.isSelectedEnd(d) && !props.isSelectedStart(d),
    'rounded-r-none':
      props.isRange &&
      props.isSelectedStart(d) &&
      !props.isSelectedEnd(d) &&
      !!props.modelRange.endDate,
  },
];
const getDayTextClasses = (d: Dayjs, isToday: boolean) => ({
  'text-primary-blue-600 font-semibold':
    isToday &&
    !(
      props.isRange &&
      (props.isWithinSelected(d) ||
        props.isSelectedStart(d) ||
        props.isSelectedEnd(d))
    ) &&
    !(props.modelSingle && props.modelSingle.isSame(d, 'day')),
  '!text-light-50':
    props.isRange &&
    (props.isWithinSelected(d) ||
      props.isSelectedStart(d) ||
      props.isSelectedEnd(d)),
});
const getDateRangeHighlightClasses = (d: Dayjs) => [
  'hidden absolute bg-primary-blue-500 h-full z-1',
  {
    '!block':
      props.isRange &&
      (props.isWithinSelected(d) ||
        props.isSelectedStart(d) ||
        props.isSelectedEnd(d)),
    'w-[150%]': props.isRange && props.isWithinSelected(d),
    'w-[80%] -right-2':
      props.isRange &&
      props.isSelectedStart(d) &&
      !!props.modelRange.endDate &&
      !props.isSelectedEnd(d),
    'w-[80%] -left-2':
      props.isRange && props.isSelectedEnd(d) && !props.isSelectedStart(d),
  },
];
</script>
<template>
  <Transition name="scale">
    <Teleport :to="appendTo">
      <div
        v-if="open"
        id="date-picker-panel"
        ref="panel"
        v-on-click-outside.stop="() => handleClickOutside()"
        class="relative"
        :style="panelStyle"
        role="dialog"
        @click.stop.prevent
      >
        <div
          class="rounded-xl border-2 border-light-400 dark:border-dark-600 shadow-md overflow-hidden"
          :class="[bgColor]"
          :style="{
            width: `${datePickerRoot?.clientWidth || 320}px`,
          }"
        >
          <div
            class="flex items-center justify-between px-2 border-b-2 border-light-400 dark:border-dark-600"
          >
            <div class="flex items-center justify-center">
              <DesignSystemButton
                type="transparent"
                shape="square"
                class="disabled:opacity-30 disabled:cursor-not-allowed disabled:!bg-transparent"
                :disabled="!canGoPrevMonth"
                :title="$t('previous-month')"
                @click="emits('prev')"
              >
                <ChevronLeftIcon class="stroke-2 !h-4" />
              </DesignSystemButton>
              <DesignSystemButton
                type="transparent"
                class="disabled:opacity-30 disabled:cursor-not-allowed disabled:!bg-transparent text-sm font-light"
                :title="`${monthNames[viewMonth]} ${viewYear}`"
                @click="emits('toggle-month')"
              >
                {{ monthNames[viewMonth] }}
              </DesignSystemButton>
              <DesignSystemButton
                type="transparent"
                shape="square"
                class="disabled:opacity-30 disabled:cursor-not-allowed disabled:!bg-transparent"
                :title="$t('next-month')"
                :disabled="!canGoNextMonth"
                @click="emits('next')"
              >
                <ChevronRightIcon class="stroke-2 !h-4" />
              </DesignSystemButton>
            </div>

            <div class="flex items-center justify-center">
              <DesignSystemButton
                type="transparent"
                shape="square"
                class="disabled:opacity-30 disabled:cursor-not-allowed disabled:!bg-transparent"
                :title="$t('prev-year')"
                :disabled="!canGoPrevYear"
                @click="emits('prev-year')"
              >
                <ChevronLeftIcon class="stroke-2 !h-4" />
              </DesignSystemButton>
              <DesignSystemButton
                type="transparent"
                class="disabled:opacity-30 disabled:cursor-not-allowed disabled:!bg-transparent text-sm font-light"
                :title="viewYear"
                @click="emits('toggle-year')"
              >
                {{ viewYear }}
              </DesignSystemButton>
              <DesignSystemButton
                type="transparent"
                shape="square"
                class="disabled:opacity-30 disabled:cursor-not-allowed disabled:!bg-transparent"
                :title="$t('next-year')"
                :disabled="!canGoNextYear"
                @click="emits('next-year')"
              >
                <ChevronRightIcon class="stroke-2 !h-4" />
              </DesignSystemButton>
            </div>
          </div>

          <div v-if="viewMode === 'months'" class="grid grid-cols-3 gap-2 p-3">
            <DesignSystemButton
              v-for="(m, idx) in monthOptions"
              :key="idx"
              type="transparent"
              class="text-sm font-light items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed disabled:!bg-transparent"
              padding="sm"
              :disabled="m.disabled"
              :title="m.label"
              @click="emits('choose-month', m.value)"
            >
              {{ m.label }}
            </DesignSystemButton>
          </div>

          <div
            v-else-if="viewMode === 'years'"
            class="grid grid-cols-4 gap-2 p-3 max-h-64 overflow-auto"
          >
            <DesignSystemButton
              v-for="y in yearOptions"
              :key="y.value"
              type="transparent"
              class="text-sm font-light items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed disabled:!bg-transparent"
              padding="sm"
              :disabled="y.disabled"
              :title="y.label"
              @click="emits('choose-year', y.value)"
            >
              {{ y.label }}
            </DesignSystemButton>
          </div>

          <div v-else class="p-2">
            <div
              class="grid grid-cols-7 text-center text-xs font-bold text-base-priority mb-1 capitalize"
            >
              <span v-for="d in dayNames" :key="d">{{ d }}</span>
            </div>

            <div class="grid grid-cols-7 gap-y-1" role="grid">
              <div
                v-for="(cell, i) in grid"
                :key="i"
                class="flex items-center justify-center relative"
              >
                <DesignSystemButton
                  :id="cell.date.format(format)"
                  :aria-selected="
                    isSelectedStart(cell.date) ||
                    isSelectedEnd(cell.date) ||
                    modelSingle?.isSame(cell.date, 'day') ||
                    false
                  "
                  role="gridcell"
                  type="transparent"
                  padding="xs"
                  rounding="md"
                  :class="getDayContainerClasses(cell.date)"
                  :title="cell.date.format(format)"
                  :disabled="cell.disabled || disabled"
                  @click="emits('day-click', cell.date)"
                  @keydown.enter.prevent="emits('day-click', cell.date)"
                >
                  <span :class="getDayTextClasses(cell.date, cell.isToday)">
                    {{ cell.date.date() }}
                  </span>
                </DesignSystemButton>
                <span :class="getDateRangeHighlightClasses(cell.date)" />
              </div>
            </div>
          </div>

          <div
            v-if="isRange && showFooterConfirm && !closeOnSelect"
            class="flex items-center justify-between px-2 py-2"
          >
            <DesignSystemButton
              class="text-xs"
              type="secondary-alternative"
              rounding="md"
              padding="tall"
              :title="$t('reset')"
              :disabled="!(modelRange.startDate || modelRange.endDate)"
              @click="emits('clear')"
            >
              {{ $t('reset') }}
            </DesignSystemButton>
            <div class="flex items-center gap-2">
              <DesignSystemButton
                class="text-xs"
                type="secondary-alternative"
                rounding="md"
                padding="tall"
                :title="$t('cancel')"
                @click="emits('cancel')"
              >
                {{ $t('cancel') }}
              </DesignSystemButton>
              <DesignSystemButton
                class="text-xs"
                type="primary"
                rounding="md"
                padding="tall"
                :title="$t('confirm')"
                :disabled="!(modelRange.startDate && modelRange.endDate)"
                @click="emits('confirm')"
              >
                {{ $t('done') }}
              </DesignSystemButton>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </Transition>
</template>
